import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { useAuthorisation } from "../hooks/useAuthorisation";

function P2POffersPage() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [listings, setListings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [showCounterModal, setShowCounterModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [counterAmount, setCounterAmount] = useState("");
  const [counterMessage, setCounterMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState(null);
  const { getAccessTokenOrSignIn } = useAuthorisation();

  const fetchOffersAndListings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Get authentication token
      const accessToken = await getAccessTokenOrSignIn();
      if (!accessToken) {
        throw new Error("Authentication required");
      }

      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL || "";

      // First, fetch all seller's listings
      const listingsResponse = await fetch(
        `${apiBaseUrl}/api/secure/p2p/listings`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!listingsResponse.ok) {
        throw new Error("Failed to fetch listings");
      }

      const listingsData = await listingsResponse.json();
      if (!listingsData.success) {
        throw new Error(listingsData.error || "Failed to fetch listings");
      }

      // Create a map of listing ID to listing data
      const listingsMap = {};
      listingsData.listings.forEach((listing) => {
        listingsMap[listing.id] = listing;
      });
      setListings(listingsMap);

      // Fetch offers for each listing
      const allOffers = [];
      for (const listing of listingsData.listings) {
        try {
          const offersResponse = await fetch(
            `${apiBaseUrl}/api/secure/p2p/listings/${listing.id}/offers`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          if (offersResponse.ok) {
            const offersData = await offersResponse.json();
            if (offersData.success && offersData.offers) {
              allOffers.push(...offersData.offers);
            }
          }
        } catch (offerError) {
          console.error(
            `Error fetching offers for listing ${listing.id}:`,
            offerError
          );
          // Continue with other listings
        }
      }

      // Sort offers by created_at descending (newest first)
      allOffers.sort((a, b) => b.created_at - a.created_at);
      setOffers(allOffers);
    } catch (err) {
      console.error("Error fetching offers:", err);
      setError(err.message || "Failed to load offers");
    }
    setIsLoading(false);
  }, [getAccessTokenOrSignIn]);

  useEffect(() => {
    fetchOffersAndListings();
  }, [fetchOffersAndListings]);

  const handleAcceptOffer = async (offerId) => {
    if (
      !window.confirm(
        "Are you sure you want to accept this offer? This will mark the listing as sold."
      )
    ) {
      return;
    }

    setIsSubmitting(true);
    setActionError(null);
    try {
      // Get authentication token
      const accessToken = await getAccessTokenOrSignIn();
      if (!accessToken) {
        throw new Error("Authentication required");
      }

      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL || "";

      const response = await fetch(
        `${apiBaseUrl}/api/secure/p2p/offers/${offerId}/accept`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to accept offer");
      }

      alert("Offer accepted successfully! The buyer will be notified.");
      fetchOffersAndListings(); // Refresh data
    } catch (err) {
      console.error("Error accepting offer:", err);
      setActionError(err.message || "Failed to accept offer");
      alert(err.message || "Failed to accept offer");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeclineOffer = async (offerId) => {
    if (!window.confirm("Are you sure you want to decline this offer?")) {
      return;
    }

    setIsSubmitting(true);
    setActionError(null);
    try {
      // Get authentication token
      const accessToken = await getAccessTokenOrSignIn();
      if (!accessToken) {
        throw new Error("Authentication required");
      }

      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL || "";

      const response = await fetch(
        `${apiBaseUrl}/api/secure/p2p/offers/${offerId}/decline`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to decline offer");
      }

      alert("Offer declined.");
      fetchOffersAndListings(); // Refresh data
    } catch (err) {
      console.error("Error declining offer:", err);
      setActionError(err.message || "Failed to decline offer");
      alert(err.message || "Failed to decline offer");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenCounterModal = (offer) => {
    setSelectedOffer(offer);
    setCounterAmount(offer.offer_amount.toString());
    setCounterMessage("");
    setShowCounterModal(true);
    setActionError(null);
  };

  const handleCloseCounterModal = () => {
    setShowCounterModal(false);
    setSelectedOffer(null);
    setCounterAmount("");
    setCounterMessage("");
    setActionError(null);
  };

  const handleSubmitCounter = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setActionError(null);

    try {
      const amount = parseFloat(counterAmount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error("Please enter a valid counter amount");
      }

      // Get authentication token
      const accessToken = await getAccessTokenOrSignIn();
      if (!accessToken) {
        throw new Error("Authentication required");
      }

      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL || "";

      const response = await fetch(
        `${apiBaseUrl}/api/secure/p2p/offers/${selectedOffer.id}/counter`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            counterAmount: amount,
            counterMessage: counterMessage || null,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to submit counter offer");
      }

      alert("Counter offer sent successfully!");
      handleCloseCounterModal();
      fetchOffersAndListings(); // Refresh data
    } catch (err) {
      console.error("Error submitting counter offer:", err);
      setActionError(err.message || "Failed to submit counter offer");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "ACCEPTED":
        return "bg-green-100 text-green-800";
      case "DECLINED":
        return "bg-red-100 text-red-800";
      case "COUNTERED":
        return "bg-blue-100 text-blue-800";
      case "WITHDRAWN":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const filteredOffers =
    statusFilter === "ALL"
      ? offers
      : offers.filter((offer) => offer.status === statusFilter);

  if (isLoading) {
    return (
      <div className="p-4 md:p-8">
        <p>Loading offers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 md:p-8">
        <div className="text-center">
          <p className="text-red-500 mb-4">Error: {error}</p>
          <Button onClick={() => navigate("/p2p/dashboard")}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Manage Offers</h1>
        <Button variant="outline" onClick={() => navigate("/p2p/dashboard")}>
          Back to Dashboard
        </Button>
      </div>

      {/* Status Filter */}
      <div className="mb-6 flex gap-2 flex-wrap">
        {[
          "ALL",
          "PENDING",
          "ACCEPTED",
          "DECLINED",
          "COUNTERED",
          "WITHDRAWN",
        ].map((status) => (
          <Button
            key={status}
            variant={statusFilter === status ? "default" : "outline"}
            onClick={() => setStatusFilter(status)}
            size="sm"
          >
            {status}
          </Button>
        ))}
      </div>

      {filteredOffers.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-gray-500">
              {statusFilter === "ALL"
                ? "No offers yet. When buyers make offers on your listings, they will appear here."
                : `No ${statusFilter.toLowerCase()} offers.`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredOffers.map((offer) => {
            const listing = listings[offer.listing_id];
            return (
              <Card key={offer.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">
                        {listing?.title || "Unknown Listing"}
                      </CardTitle>
                      <p className="text-sm text-gray-600">
                        Listing Price: ${listing?.price?.toFixed(2) || "N/A"}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(
                        offer.status
                      )}`}
                    >
                      {offer.status}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Offer Amount</p>
                      <p className="text-2xl font-bold">
                        ${offer.offer_amount.toFixed(2)}
                      </p>
                    </div>
                    {offer.counter_amount && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1">
                          Your Counter Offer
                        </p>
                        <p className="text-2xl font-bold text-blue-600">
                          ${offer.counter_amount.toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>

                  {offer.message && (
                    <div className="mb-4 p-3 bg-gray-50 rounded">
                      <p className="text-sm text-gray-600 mb-1">
                        Buyer Message:
                      </p>
                      <p className="text-gray-800">{offer.message}</p>
                    </div>
                  )}

                  {offer.counter_message && (
                    <div className="mb-4 p-3 bg-blue-50 rounded">
                      <p className="text-sm text-gray-600 mb-1">
                        Your Counter Message:
                      </p>
                      <p className="text-gray-800">{offer.counter_message}</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span>
                      Received:{" "}
                      {new Date(offer.created_at * 1000).toLocaleString()}
                    </span>
                    <Button
                      variant="link"
                      onClick={() =>
                        navigate(`/p2p/listing/${offer.listing_id}`)
                      }
                      className="text-sm"
                    >
                      View Listing
                    </Button>
                  </div>

                  {/* Action buttons - only show for PENDING offers */}
                  {offer.status === "PENDING" && (
                    <div className="flex gap-3">
                      <Button
                        onClick={() => handleAcceptOffer(offer.id)}
                        disabled={isSubmitting}
                        className="flex-1"
                      >
                        Accept
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleOpenCounterModal(offer)}
                        disabled={isSubmitting}
                        className="flex-1"
                      >
                        Counter Offer
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleDeclineOffer(offer.id)}
                        disabled={isSubmitting}
                        className="flex-1 text-red-600 hover:text-red-700"
                      >
                        Decline
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Counter Offer Modal */}
      {showCounterModal && selectedOffer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6 relative">
            <button
              onClick={handleCloseCounterModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              disabled={isSubmitting}
            >
              &times;
            </button>

            <h2 className="text-2xl font-bold mb-4">Make Counter Offer</h2>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-1">
                Buyer&apos;s Offer: ${selectedOffer.offer_amount.toFixed(2)}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                Listing Price: $
                {listings[selectedOffer.listing_id]?.price?.toFixed(2) || "N/A"}
              </p>
            </div>

            <form onSubmit={handleSubmitCounter}>
              <div className="mb-4">
                <label
                  htmlFor="counterAmount"
                  className="block text-sm font-medium mb-2"
                >
                  Your Counter Offer Amount ($)
                </label>
                <input
                  type="number"
                  id="counterAmount"
                  step="0.01"
                  min="0.01"
                  value={counterAmount}
                  onChange={(e) => setCounterAmount(e.target.value)}
                  required
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your counter offer"
                />
              </div>

              <div className="mb-4">
                <label
                  htmlFor="counterMessage"
                  className="block text-sm font-medium mb-2"
                >
                  Message (Optional)
                </label>
                <textarea
                  id="counterMessage"
                  value={counterMessage}
                  onChange={(e) => setCounterMessage(e.target.value)}
                  rows={3}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Add a message to the buyer..."
                />
              </div>

              {actionError && (
                <p className="text-red-500 text-sm mb-4">{actionError}</p>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseCounterModal}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  {isSubmitting ? "Submitting..." : "Send Counter Offer"}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}

export default P2POffersPage;
